<template>
  <div class="customer-applications">
    <div class="container-fluid p-2">
      <div class="row g-2">
        <div class="col-sm-12">
          <div class="d-flex align-items-center mb-3">
            <h5 class="fs-6 text-muted fw-bold mb-0 me-auto">Customer Applications</h5>
          </div>
          <hr />

          <!-- Display Total Risk Score -->
          <CumulativeRiskScore :entity-id="entity?.id" />

          <div v-if="error" class="alert alert-danger py-2 px-3 mb-3 text-xs">{{ error }}</div>
          <div v-else-if="!applications || applications.length === 0" class="text-muted p-3">
            No applications to display.
          </div>

          <div v-else class="position-relative">
            <div 
              v-for="(app, index) in applications" 
              :key="key_for(app)" 
              class="card rounded-3 mb-3 application-card shadow-sm"
              :style="{ animationDelay: `${index * 0.05}s` }"
            >
              <div class="card-body p-4">
                <div class="d-flex justify-content-between">
                  <!-- Left Side: Icon & Info -->
                  <div class="d-flex gap-4 align-items-center">
                    <div class="application-icon flex-shrink-0">
                      <i class="fas fa-file-alt"></i>
                    </div>
                    <div>
                      <h6 class="fw-bold text-dark mb-1 fs-6">{{ app.name || app.application_name || app.title }}</h6>
                      <p class="text-sm text-secondary mb-0 text-truncate-2">{{ app.description || app.application_description || 'Application' }}</p>
                    </div>
                  </div>

                  <!-- Right Side: Meta & Actions -->
                  <div class="d-flex flex-column align-items-end justify-content-between gap-3">
                    <span v-if="app.date_created" class="badge bg-light text-secondary border fw-medium rounded-pill px-3">
                      {{ format_date_time(app.date_created) }}
                    </span>
                    
                    <div class="d-flex align-items-center gap-2">
                       <button 
                        class="btn btn-action-subtle btn-sm" 
                        @click="handleOpenNotes(app)"
                        title="Open Notes"
                      >
                        <i class="bi bi-journal-text me-2"></i>Notes
                      </button>
                      <button 
                        class="btn btn-action-primary btn-sm" 
                        @click="toggle(app)"
                        :aria-expanded="is_open(app)"
                      >
                        {{ is_open(app) ? 'Collapse' : 'Expand' }}
                      </button>
                    </div>
                  </div>
                </div>

                <div v-if="is_open(app)" class="mt-3 application-body">
                  <!-- loader -->
                  <div v-if="loading" class="text-center py-4">
                    <div class="spinner-border spinner-border-sm text-primary" role="status">
                      <span class="visually-hidden">Loading...</span>
                    </div>
                    <p class="text-muted text-xs mt-2 mb-0">Loading application data...</p>
                  </div>
                  
                    <div v-else-if="normalizedEntityType && normalizedEntityType === 'individual'">
                    <CustomTabs
                      :tabs="main_tabs"
                      v-model="current_tab"
                    />
                    <div class="tab-content" v-if="current_tab === 'Profile'">
                      <CustomerProfile
                        :first_name="first_name"
                        :last_name="last_name"
                        :email="email"
                        :phone_number="phone_number"
                        :reference="reference"
                        :id_number="id_number"
                        :workflow_id="workflow_id"
                        :date_joined="date_joined"
                        :get_user_id="selected_customer_id"
                        :customer_application="selected_customer_application"
                        :credit_bureau_services="credit_bureau_services"
                        :financial_services="financial_services"
                        :client="client"
                        @service-clicked="handleServiceClick($event, app)"
                        :ref="el => { if (el) customerProfileRefs[app.id] = el }"
                        />
                    </div>
                    <div v-if="current_tab === 'AuditTrail'" class="tab-content">
                      <AuditTrail
                        :workflow_id="workflow_id"
                        :audit_trails="audit_trails"
                      />
                    </div>
                    <div v-if="current_tab === 'Services'" class="tab-content">
                      <CustomerApplicationServices
                        :services="app.services"
                        :application="app"
                        :entity="entity"
                        :entity-type="entityType"
                        :financial_services="financial_services"
                        :ref="el => { if (el) customerApplicationServicesRefs[app.id] = el }"
                        @seriti-finance-click="handleSeritiFinanceClick(app)"
                      />
                    </div>
                  </div>

                  <!-- Company Entity without Tabs -->
                  <div v-else-if="normalizedEntityType && normalizedEntityType === 'company'">
                    <div v-if="hasRiskSummary">
                      <div class="card border-2 bg-white rounded-3 shadow-sm h-100 mb-3">
                        <div class="p-3">
                          <h6 class="text-uppercase text-sm fw-bold border-bottom pb-2 mb-3">
                            Risk Summary
                          </h6>
                          <RiskSummary
                            :services="filteredServices"
                            :loading="riskDataMap.risk_loading"
                            @service-clicked="(service) => handleServiceClick(service, app)"
                          />
                        </div>
                      </div>
                    </div>

                    <CustomerApplicationServices
                      :services="app.services"
                      :application="app"
                      :entity="entity"
                      :entity-type="entityType"
                      :ref="el => { if (el) customerApplicationServicesRefs[app.id] = el }"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Notes Modal -->
    <Teleport to="body">
      <transition name="notes-modal-fade">
        <div v-if="showNotesModal" class="notes-modal-overlay" @click.self="closeNotesModal">
          <div class="notes-modal-container" @click.stop>
            <div class="notes-modal-header">
              <div class="flex-grow-1"></div>
              <button class="btn btn-sm btn-close" @click="closeNotesModal"></button>
            </div>
            <div class="notes-modal-body">
              <NoteTaker v-if="notesModalReady" :key="`notes-${selectedNoteAppId}`" :application-id="selectedNoteAppId" />
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import CustomerApplicationServices from "@/layouts/data_hub/components/customer_applications/CustomerApplicationServices.vue";
import CumulativeRiskScore from "@/components/CumulativeRiskScore/CumulativeRiskScore.vue";
import NoteTaker from "@/components/NoteTaker/NoteTaker.vue";
import { API_BASE_URL } from "@/config";
import { format_date_time } from "@/utils/date_formatter";
import CustomerProfile from "@/layouts/client/customer_data/components/CustomerProfile.vue";
import AuditTrail from "@/layouts/client/customer_data/components/AuditTrail.vue";
import CustomTabs from '@/components/CustomTab/CustomTab.vue';
import { fetchCustomerData } from "@/utils/fetch_customer_data.js";
import { defineAsyncComponent } from "vue";

export default {
  name: "CustomerApplications",
  components: {
    CustomerApplicationServices,
    CustomTabs,
    CustomerProfile,
    AuditTrail,
    CumulativeRiskScore,
    NoteTaker,
    RiskSummary: defineAsyncComponent({
      loader: () => import("@/components/RiskMatrix/RiskSummary.vue"),
      delay: 200,
    }),
  },
  computed: {
    normalizedEntityType() {
      if (!this.entityType) return null;
      const t = String(this.entityType).toLowerCase();
      if (t === 'enterprise') return 'company';
      return t;
    },
    filteredServices() {
      if (
        !this.riskDataMap.risk_data ||
        !Array.isArray(this.riskDataMap.risk_data)
      ) {
        return [];
      }
      return this.riskDataMap.risk_data.filter(
        (service) =>
          service.severity && service.severity.toLowerCase() !== "unknown"
      );
    },
    hasRiskSummary() {
      return this.filteredServices.length > 0 || this.riskDataMap.risk_loading;
    },
    totalScore() {
      return this.filteredServices.reduce(
        (total, service) => total + (service.total_score || 0),
        0
      );
    },
    totalMaxScore() {
      return this.filteredServices.reduce(
        (total, service) => total + (service.max_possible_score || 0),
        0
      );
    },
    overallPercentage() {
      if (this.totalMaxScore === 0) return 0;
      return Math.round((this.totalScore / this.totalMaxScore) * 100);
    },
    overallPercentageClass() {
      return `severity-${this.overallSeverity}`;
    }
  },
  props: {
    entity: { type: Object, required: false, default: null },
    entityType: { type: String, required: false, default: null },
    selectedApplicationId: { type: [String, Number], required: false, default: null },
  },
  data() {
    return {
      current_tab: "Services",
      open_key: null,
      applications: [],
      loading: false,
      error: null,
      customerApplicationServicesRefs: {},
      customerProfileRefs: {},
      workflow_id: null,
      audit_trails: [],
      first_name: "",
      last_name: "",
      email: "",
      reference: "",
      phone_number: "",
      selected_customer_id: null,
      selected_customer_application: null,
      date_joined: null,
      credit_bureau_services: [],
      financial_services: {},
      client: localStorage.getItem('client'),
      id_number: "",
      riskDataByApp: {},
      riskDataMap: { risk_loading: false, risk_data: [], risk_error: null },
      showNotesModal: false,
      selectedNoteAppId: null,
      notesModalReady: false,
      main_tabs: [
        { label: 'Profile', value: 'Profile' },
        { label: 'Audit Trail', value: 'AuditTrail' },
        { label: 'Services', value: 'Services' }
      ],
    };
  },
  methods: {
    format_date_time,
    closeNotesModal() {
      this.showNotesModal = false;
      setTimeout(() => {
        this.notesModalReady = false;
      }, 200);
    },
    getEntityId(entity) {
      if (!entity) return null;
      return entity.id ?? entity.entity_id ?? entity.identifier ?? entity.raw?.id ?? entity.entity_information?.customer_id ?? null;
    },
    key_for(app) {
      return app.id ?? app.name ?? app.application_name ?? app.title ?? null;
    },
    toggle(app) {
      const k = this.key_for(app);
      const opening = this.open_key !== k;
      this.open_key = opening ? k : null;
    },
    is_open(app) {
      const k = this.key_for(app);
      // Allow keys that may be falsy values (like 0). Compare directly
      // instead of coercing to boolean which would reject valid keys.
      return this.open_key === k;
    },
    async fetchApplications() {
      // allow entity.id === 0 and handle other shapes (entity_id, identifier, primary_entity)
      const entity_id = this.getEntityId(this.entity);
      if (entity_id == null) {
        this.applications = [];
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        const TOKEN = localStorage.getItem('token');
        if (!TOKEN) throw new Error('Missing auth token');
        const url = `${API_BASE_URL}/verify_api/data_hub/entities/${entity_id}/workflows/services/`;
        const resp = await fetch(url, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${TOKEN}`,
          },
        });
        if (!resp.ok) {
          throw new Error(`Failed to fetch applications (${resp.status})`);
        }
        const payload = await resp.json();
        const apps_src = (payload && Array.isArray(payload.applications)) ? payload.applications : [];
        this.applications = apps_src.map((app, idx) => {
          const workflows = Array.isArray(app.workflows) ? app.workflows : [];
          const aggregated_services = workflows.flatMap(wf => {
            const svcs = Array.isArray(wf.services) ? wf.services : [];
            return svcs.map(s => ({
              id: s.id,
              service_name: s.name || s.service_name || `Service ${s.id || ''}`.trim(),
              service_description: s.description || s.type || '',
              service_type: s.type || null,
              raw: s,
              workflow_id: wf.workflow_id,
              workflow_name: wf.workflow_name,
            }));
          });
          return {
            id: app.id || idx + 1,
            name: (workflows[0] && workflows[0].workflow_name) || app.name || app.application_name || `Application ${idx + 1}`,
            description: (workflows[0] && workflows[0].workflow_description) || app.description || app.application_description || '',
            services: aggregated_services.length ? aggregated_services : [],
            workflows,
            date_created: app.date_created || null,
            raw: app,
          };
        });

        if (this.applications.length > 0) {
          // If selectedApplicationId is provided, find and expand that application
          let targetApp = null;
          if (this.selectedApplicationId !== null && this.selectedApplicationId !== undefined && this.selectedApplicationId !== '') {
              targetApp = this.applications.find(app => 
                app.id === this.selectedApplicationId || 
                app.id === parseInt(this.selectedApplicationId)
              );
            }
          // Fall back to first application if not found
          if (!targetApp) {
            targetApp = this.applications[0];
          }
          
          this.open_key = this.key_for(targetApp);
          // Try multiple sources for customer_id
          this.selected_customer_id = targetApp.raw?.customer_id 
            || targetApp.customer_id 
            || this.entity?.customer_id 
            || this.entity?.entity_information?.customer_id
            || null;
          this.selected_customer_application = targetApp.id;
          this.workflow_id = targetApp.workflows?.[0]?.workflow_id || null;
          
          // Trigger data loading
          this.$nextTick(() => {
            if (this.normalizedEntityType === 'individual' && this.selected_customer_id != null) {
              this.get_customer_data();
            }
            if (this.workflow_id != null && this.selected_customer_application != null) {
              this.loadApplicationRiskData();
            }
          });
        }
      } catch (e) {
        this.error = e.message || String(e);
      } finally {
        this.loading = false;
      }
    },
    getPercentage(service) {
      if (!service.max_possible_score || service.max_possible_score === 0) return 0;
      return Math.round((service.total_score / service.max_possible_score) * 100);
    },
    getSeverityClass(severity) {
      return `severity-${severity ? String(severity).toLowerCase() : 'unknown'}`;
    },
    handleServiceClick(service, app) {
      if (this.normalizedEntityType === 'individual') {
        this.current_tab = 'Services';
      }

      this.$nextTick(() => {
        const serviceComponent = this.customerApplicationServicesRefs[app.id];
        if (serviceComponent && service) {
          if (this.normalizedEntityType === 'company' && service.service_id) {
            const matchingService = app.services.find(s => s.id === service.service_id);
            if (matchingService) {
              serviceComponent.selectServiceAndShowRisk(matchingService);
            }
          } else {
            serviceComponent.selectServiceAndShowRisk(service);
          }
        }
      });
    },
    handleSeritiFinanceClick(app) {
      this.current_tab = 'Profile';
      
      this.$nextTick(() => {
        const customerProfile = this.customerProfileRefs[app.id];
        if (customerProfile && typeof customerProfile.openSeritiPolicy === 'function') {
          customerProfile.openSeritiPolicy();
        }
      });
    },
    openSeritiPolicyDirect() {
      this.current_tab = 'Profile';
      
      this.$nextTick(() => {
        const profileRefs = Object.values(this.customerProfileRefs);
        if (profileRefs.length > 0) {
          const customerProfile = profileRefs[0];
          if (customerProfile && typeof customerProfile.openSeritiPolicy === 'function') {
            customerProfile.openSeritiPolicy();
          }
        }
      });
    },
    getSeverityBadgeColor(severity) {
      switch (severity?.toLowerCase()) {
        case 'critical': 
        case 'high': return 'danger';
        case 'medium': return 'warning';
        case 'low': return 'success';
        default: return 'secondary';
      }
    },
    getRiskIcon(severity) {
      switch (severity?.toLowerCase()) {
        case 'critical':
        case 'high': return 'warning';
        case 'medium': return 'info';
        case 'low': return 'success';
        default: return 'question';
      }
    },
    async get_customer_data() {
      const token = localStorage.getItem("token");
      const customer_id = this.selected_customer_id;
      const customer_application = this.selected_customer_application;
      try {
        const data = await fetchCustomerData({ customer_id, customer_application, token });

        this.id_number = data.id_number;
        this.first_name = data.first_name;
        this.last_name = data.last_name;
        this.email = data.email;
        this.reference = data.reference;
        this.phone_number = data.phone_number;
        this.date_joined = data.date_joined;
        this.audit_trails = data.audit_trails;
        
        // Fetch service responses for Zoho integration
        this.get_customer_service_responses();
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: error.responseJSON.error,
          confirmButtonText: "OK",
        });
      }
    },
    get_customer_service_responses() {
      const token = localStorage.getItem("token");
      const customer_id = this.selected_customer_id;
      const customer_application = this.selected_customer_application;

      if (!customer_id || !customer_application) {
        return;
      }

      const get_service_responses = `${API_BASE_URL}/verify_api/workflow_management_api/get_customer_service_responses/${customer_id}/${customer_application}/`;

      $.ajax({
        method: "GET",
        url: get_service_responses,
        contentType: "application/json",
        headers: {
          Authorization: `Token ${token}`,
        },
        success: (response) => {
          this.credit_bureau_services = response.credit_bureau || [];
          this.financial_services = response.financial_services || {};
        },
        error: (xhr, status, error) => {
          console.error("Error fetching customer service responses:", error);
          this.credit_bureau_services = [];
          this.financial_services = {};
        },
      });
    },
    loadApplicationRiskData() {
      const TOKEN = localStorage.getItem("token");

      if (!this.workflow_id || !this.selected_customer_application) {
        console.warn('Missing workflow_id or selected_customer_application, aborting loadApplicationRiskData');
        return;
      }
      this.riskDataMap.risk_loading = true;

      $.ajax({
        url: `${API_BASE_URL}/verify_api/workflow_management_api/customers_scores/`,
        method: "GET",
        contentType: "application/json",
        headers: { Authorization: `Token ${TOKEN}` },
        data: {
          workflow_id: this.workflow_id,
          customer_application: this.selected_customer_application,
        },
        success: (response) => {
          if (
            response.status === "success" &&
            response.data &&
            response.data.length
          ) {
            const latestApp = response.data.reduce((latest, app) => {
              return !latest || app.application_id > latest.application_id
                ? app
                : latest;
            }, null);

            this.riskDataMap = {
              risk_loading: false,
              risk_data: latestApp?.services || [],
              risk_error: latestApp ? null : "No risk data",
            };
          } else {
            this.riskDataMap = {
              risk_loading: false,
              risk_data: [],
              risk_error: "No risk data",
            };
          }
        },
        error: () => {
          this.riskDataMap = {
            risk_loading: false,
            risk_data: [],
            risk_error: "Failed to load risk data",
          };
        },
      });
    },
    handleOpenNotes(app) {
      // Allow app.id == 0
      if (app && app.id !== null && app.id !== undefined) {
        this.selectedNoteAppId = app.id;
        this.showNotesModal = true;

        this.$nextTick(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.notesModalReady = true;
            });
          });
        });
      }
    },
  },
  watch: {
    // Watch the whole entity object because Workbench sometimes passes a primary_entity
    // object that may not have `id` but has `entity_id` or other fields.
    entity: {
      handler(newEntity) {
        const eid = this.getEntityId(newEntity);
        if (eid !== null && eid !== undefined) {
          this.fetchApplications();
        } else {
          // clear if entity removed
          this.applications = [];
        }
      },
      immediate: true,
      deep: false,
    },
    selectedApplicationId: {
      handler(newId, oldId) {
        // Re-fetch when selectedApplicationId changes (for Workbench - switching between applications)
        // But only if entity is already set (to avoid double-fetch on mount)
        if (newId !== null && newId !== undefined && newId !== oldId && this.entity?.id != null) {
          this.fetchApplications();
        }
      },
      immediate: false,
    },
    open_key: {
      handler(newKey) {
        if (newKey !== null && newKey !== undefined) {
          const openApp = this.applications.find(app => this.key_for(app) === newKey);
          if (openApp) {
            this.selected_customer_application = openApp.id;
            // Try multiple sources for customer_id
            this.selected_customer_id = openApp.raw?.customer_id 
              || openApp.customer_id 
              || this.entity?.customer_id 
              || this.entity?.entity_information?.customer_id
              || null;
            this.workflow_id = openApp.workflows?.[0]?.workflow_id || null;

            if (this.normalizedEntityType === 'individual' && this.selected_customer_id) {
              this.get_customer_data();
            }
            if (this.workflow_id && this.selected_customer_application) {
              this.loadApplicationRiskData();
            }
          }
        }
      },
      immediate: false,
    },
    workflow_id: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined && this.selected_customer_application !== null && this.selected_customer_application !== undefined) {
          this.loadApplicationRiskData();
        }
      },
      immediate: false,
    },
    selected_customer_application: {
      handler(newVal) {
        if (newVal !== null && newVal !== undefined && this.workflow_id !== null && this.workflow_id !== undefined) {
          this.loadApplicationRiskData();
        }
      },
      immediate: false,
    },
  },
};
</script>

<style scoped>

.application-card {
  border: 1px solid #e2e8f0;
  border-left: 0;
  background: #ffffff;
  transition: transform 0.2s cubic-bezier(0.25, 0.8, 0.25, 1), 
              box-shadow 0.2s cubic-bezier(0.25, 0.8, 0.25, 1),
              border-color 0.2s ease;
  animation: slideInUp 0.4s ease-out backwards;
  position: relative;
  overflow: hidden;
}

/* Accent Strip */
.application-card::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  background: #10b981;
  z-index: 2;
}

.application-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px -10px rgba(0, 0, 0, 0.1) !important;
  border-color: #cbd5e1;
}

.application-body {
  position: relative;
}

.customer-applications .application-icon {
  width: 44px;
  height: 44px;
  background: #ecfdf5;
  color: #10b981;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1fae5;
  font-size: 1.1rem;
}

.text-truncate-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-width: 600px;
}

/* Button Actions */
.btn-action-primary {
  background: #1e293b; /* Dark Navy */
  color: #fff;
  border-radius: 6px;
  padding: 6px 16px;
  font-weight: 500;
  font-size: 0.85rem;
  transition: all 0.2s;
  min-width: 80px;
  border: none;
}

.btn-action-primary:hover {
  background: #0f172a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  color: #fff;
}

.btn-action-subtle {
  background: #ecfdf5; /* Light Green */
  color: #10b981; /* Green text */
  border: 1px solid #a7f3d0;
  border-radius: 6px;
  padding: 6px 16px;
  font-weight: 600;
  font-size: 0.85rem;
  transition: all 0.2s;
}

.btn-action-subtle:hover {
  background: #d1fae5;
  color: #059669;
  border-color: #34d399;
  transform: translateY(-1px);
}

.btn-expand-action:hover {
  background: #e2e8f0;
  color: #1e293b;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
}

.btn-expand-action:active {
  transform: scale(0.95);
}

/* Risk Assessment Modal Styles */
:deep(.risk-assessment-modal) {
  font-family: 'Segoe UI', system-ui, sans-serif;
}

:deep(.risk-detail-modal) {
  text-align: left;
}

:deep(.risk-detail-modal .service-header) {
  padding: 15px 0;
  border-bottom: 1px solid #e9ecef;
}

:deep(.risk-detail-modal .service-header h4) {
  color: #2c3e50;
  margin-bottom: 8px;
}

:deep(.risk-detail-modal .risk-metrics) {
  padding: 20px 0;
}

:deep(.risk-detail-modal .metric-item) {
  font-size: 14px;
  color: #495057;
  line-height: 1.5;
}

:deep(.risk-detail-modal .metric-item strong) {
  color: #2c3e50;
  font-weight: 600;
}

.content-loading-overlay {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 10;
  padding: 8px;
}
</style>

<!-- Global styles for teleported notes modal -->
<style>
/* Notes Modal Overlay */
.notes-modal-overlay {
  position: fixed !important;
  inset: 0 !important;
  background: rgba(15, 23, 42, 0.75) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  z-index: 10100 !important;
  padding: 20px;
}

/* Notes Modal Container */
.notes-modal-container {
  width: 100%;
  max-width: 1400px;
  max-height: 95vh;
  background: #f8fafc;
  border-radius: 20px;
  box-shadow: 0 25px 60px -15px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.notes-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.notes-modal-header h5 {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.notes-modal-body {
  flex: 1;
  overflow: hidden;
  -webkit-overflow-scrolling: touch;
}

.notes-modal-body .notes-app {
  min-height: 80vh;
  border-radius: 0;
}

/* Notes Modal Animation */
.notes-modal-fade-enter-active {
  transition: opacity 0.25s ease-out;
}

.notes-modal-fade-leave-active {
  transition: opacity 0.2s ease-in;
}

.notes-modal-fade-enter-from,
.notes-modal-fade-leave-to {
  opacity: 0;
}

.notes-modal-fade-enter-active .notes-modal-container {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease-out;
}

.notes-modal-fade-leave-active .notes-modal-container {
  transition: transform 0.2s ease-in, opacity 0.2s ease-in;
}

.notes-modal-fade-enter-from .notes-modal-container {
  transform: scale(0.95) translateY(20px);
  opacity: 0;
}

.notes-modal-fade-leave-to .notes-modal-container {
  transform: scale(0.98) translateY(10px);
  opacity: 0;
}
</style>
